import { useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import AddInputs from "../../../components/Add/AddInputs";
import AddModel from "../../../components/Add/AddModel";
import AddBottomButton from "../../../components/Add/AddBottomButton";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { useEffect, useState } from "react";
import useApi from "../../../services/useApi";
import { showToast } from "../../../components/toastMessage/toastSlice";
import endpoints from "../../../services/endpoints";
import { formEmptyValidation } from "../../../components/commonfunctions/formEmptyValidation";
import useTranslation from "../../../components/CustomHooks/useTranslation";
import { inputFieldContent } from "../../../components/inputfields/InputFieldContent";
import {
  setUpdateProfileStates,
  setProfileError,
  setProfileLoader,
  setEditData,
  resetAdminProfile,
  AdminProfileDetails,
} from "../../../store/slices/adminProfileSlice";
import { setErrorMessage, setOtp } from "../../auth/otpSlice";
import { setAddAdminError, setAddAdminFelidError, setIsVerifiedFirst } from "../../AdminManagement/addAdminSlice";
import { setLoading } from "../../auth/loginSlice";
import { setInstructionText, setIsOpenModel } from "../../../store/slices/modelSlice";
import deepEqual from "../../../components/commonfunctions/deepEqual";

export default function UpdateAdminProfile() {
  const dispatch = useDispatch();
  const { profileDetails, errors, loader, disableAll, editData } = useSelector((state: RootState) => state.adminProfile);
  const [isOpenOtpModel, setIsOpenOtpModel] = useState(false);
  const [adminId, setAdminId] = useState("");
  const { otp } = useSelector((state: any) => state.otp);
  const { selectedOTPid } = useSelector((state: any) => state.model);
  const navigation = useNavigate();
  const { id } = useParams();
  const [buttonLoader, setButtonLoader] = useState(false);
  const { PostAPI, PatchAPI, getAPI } = useApi();
  const { translate, replace } = useTranslation(inputFieldContent);
  const { otpSend, otpVerify } = endpoints;
  const userEmail = useSelector((state: RootState) => state.userEmail.email);

  useEffect(() => {
    async function fetchProfile() {
      if (userEmail) {
        try {
          const res = await getAPI(`admins/email/${userEmail}`);
          if (res && res.data) {
            const d = res.data.data;
            setAdminId(d.id);
            const fullPhoneNumber = d.country_code && d.phone_number && !d.phone_number.startsWith(d.country_code)
              ? `${d.country_code}${d.phone_number}`
              : d.phone_number || "";
            const details = {
              user_role: d.user_code?.startsWith("SADM") ? "Super Admin" : "Admin",
              user_code: d.user_code || "",
              first_name: d.first_name || "",
              last_name: d.last_name || "",
              country_code: d.country_code || "",
              phone_number: fullPhoneNumber,
              house_no: d.house_no || "",
              floor_no: d.floor_no || "",
              market_block_no: d.market_block_no || "",
              street_name: d.street_name || "",
              city_town: d.city_town || "",
              district: d.district || "",
              region: d.region || "",
              province: d.province || "",
              country: d.country || "",
              postal_code: d.postal_code || "",
              email_address: userEmail || "",
              wallet_access: (d.wallet_access || []) as string[],
              phone_number_verify: d.phone_number ? "true" : "",
            };
            dispatch(setEditData(details));
            Object.entries(details).forEach(([key, value]) => {
              dispatch(setUpdateProfileStates({ key, value }));
            });
          }
        } catch (e) {
          // handle error
        }
      }
    }
    fetchProfile();
    return () => { dispatch(resetAdminProfile()); };
  }, [userEmail]);

  const handleInputValues = (key: string, value: string) => {
    if (key === 'first_name' || key === 'last_name') {
      const regex = /^[A-Za-z\s]*$/;
      if (!regex.test(value)) {
        return;
      }
    }
    if (key === "phone_number") {
      dispatch(setUpdateProfileStates({ key: "phone_number", value: `+${value}` }));
      dispatch(setProfileError({ phone_number: "" }));
      const isUnchanged = editData.phone_number === `+${value}`;
      dispatch(setUpdateProfileStates({ key: "phone_number_verify", value: isUnchanged ? "true" : "" }));
      return;
    }
    if (key === "country_code") {
      dispatch(setUpdateProfileStates({ key: "country_code", value: `+${value}` }));
      const isUnchanged = editData.country_code === `+${value}`;
      dispatch(setUpdateProfileStates({ key: "phone_number_verify", value: isUnchanged ? "true" : "" }));
      return;
    }
    dispatch(setUpdateProfileStates({ key, value }));
  };

  const handleVerify = async (id: string, resend: boolean) => {
    const errors: Record<string, string> = {};
    if (id === "phone_number") errors.phone_number = "";
    if (id === "email_address") errors.email_address = "";
    dispatch(setAddAdminError(errors));
    try {
      dispatch(setProfileLoader({ key: id, value: true }));
      const payload =
        id === "phone_number"
          ? {
            phone_number: profileDetails.phone_number.startsWith("+") ? profileDetails.phone_number : `+${profileDetails.phone_number}`,
            name: profileDetails.first_name,
          }
          : {
            email_address: `${profileDetails.email_address}`,
            name: profileDetails.first_name,
          };
      const response = await PostAPI(otpSend, payload);
      if (!response.error) {
        setIsOpenOtpModel(true);
        dispatch(
          showToast({
            type: "info",
            message: resend
              ? "OTP resent"
              : replace(`OTP has been sent to {{phone_or_email}}`, {
                phone_or_email:
                  id === "phone_number" ? (profileDetails.phone_number.startsWith("+") ? profileDetails.phone_number : `+${profileDetails.phone_number}`) : profileDetails.email_address
                ,
              }),
          })
        );
        dispatch(setIsOpenModel(true));
        dispatch(
          setInstructionText({
            text: replace(`We have sent a code to {{phone_or_email}}`, {
              phone_or_email:
                id === "phone_number" ? (profileDetails.phone_number.startsWith("+") ? profileDetails.phone_number : `+${profileDetails.phone_number}`) : profileDetails.email_address,
            }),
            id,
          })
        );
      } else {
        dispatch(setAddAdminFelidError(id));
        dispatch(
          showToast({
            type: "error",
            message: response?.data?.response?.data?.message,
          })
        );
      }
    } catch (error) {
      dispatch(setAddAdminFelidError(id));
      console.error("Error sending OTP:", error);
      dispatch(
        showToast({
          type: "error",
          message: "Failed to send OTP. Please try again.",
        })
      );
    } finally {
      dispatch(setProfileLoader({ key: id, value: false }));
    }
  };

  const handleChanges = (data1: AdminProfileDetails, data2: AdminProfileDetails) => {
    let res = deepEqual(data1, data2)
    return res
  }

  const handleSubmitOTP = async () => {
    dispatch(setLoading(true));
    const payload =
      selectedOTPid === "phone_number"
        ? {
          phone_number: profileDetails.phone_number.startsWith("+") ? profileDetails.phone_number : `+${profileDetails.phone_number}`,
          otp: otp.join(""),
        }
        : {
          email_address: profileDetails.email_address,
          otp: otp.join(""),
        };

    try {
      const res = await PostAPI(otpVerify, payload);
      if (!res.error) {
        handleInputValues(
          selectedOTPid === "phone_number"
            ? "phone_number_verify"
            : "email_address_verify",
          "true" // Assuming value should be "true"
        );
        setIsOpenOtpModel(false)
        dispatch(setOtp({ otp: [] }));
        dispatch(setIsVerifiedFirst(false))
      } else {
        dispatch(setErrorMessage(res.data.response.data.message || "Invalid OTP."));
      }
    } catch (error: any) {
      console.error("Error verifying OTP:", error);
      dispatch(setErrorMessage("Invalid OTP. Please try again."));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Utility to get only changed fields (with formatting)
  function getChangedFields(original: any, updated: any) {
    const changed: any = {};
    Object.keys(updated).forEach(key => {
      if (updated[key] !== original[key]) {
        changed[key] = updated[key];
      }
    });
    return changed;
  }

  const handleSubmit = async () => {
    dispatch(setProfileLoader({ key: "phone_number", value: true }));
    try {
      const mandatoryField: any = {
        first_name: profileDetails.first_name,
        email_address: profileDetails.email_address,
        phone_number: profileDetails.phone_number,
        country: profileDetails.country,
      };
      if ((profileDetails.country || '').toLowerCase() === "afghanistan") {
        mandatoryField.province = profileDetails.province;
        if (profileDetails.province) mandatoryField.district = profileDetails.district;
        if (profileDetails.district) mandatoryField.city_town = profileDetails.city_town;
      } else {
        mandatoryField.province = profileDetails.province;
        mandatoryField.postal_code = profileDetails.postal_code;
      }
      const emptyFieldErrors = formEmptyValidation(mandatoryField);
      if (Object.keys(emptyFieldErrors).length > 0) {
        dispatch(setProfileError(emptyFieldErrors));
        return;
      }
      if (!profileDetails.phone_number_verify) {
        dispatch(setProfileError({ phone_number: translate("Please verify your phone number") }));
        return;
      }
      // --- Formatting logic for both editData and profileDetails ---
      const formatProfile = (data: any) => {
        let phone_number = data.phone_number || "";
        let country_code = data.country_code || "";
        if (country_code && phone_number.startsWith(country_code)) {
          phone_number = phone_number.slice(country_code.length);
        }
        if (phone_number.startsWith("+")) {
          phone_number = phone_number.slice(1);
        }
        return {
          first_name: data.first_name?.trim(),
          last_name: data.last_name?.trim() || undefined,
          phone_number: phone_number.trim(),
          country: data.country?.trim(),
          city_town: data.city_town?.trim() || undefined,
          house_no: data.house_no?.trim() || undefined,
          market_block_no: data.market_block_no?.trim() || undefined,
          floor_no: data.floor_no?.trim() || undefined,
          street_name: data.street_name?.trim() || undefined,
          province: data.province?.trim(),
          region: data.region?.trim() || undefined,
          district: data.district?.trim() || undefined,
          postal_code: data.postal_code?.trim() || undefined,
          country_code: country_code.trim(),
        };
      };
      const formattedProfile = formatProfile(profileDetails);
      const formattedEdit = formatProfile(editData);
      // Get only changed fields
      const changedFields = getChangedFields(formattedEdit, formattedProfile);
      if (Object.keys(changedFields).length === 0) {
        dispatch(
          showToast({
            type: "info",
            message: translate('No changes detected')
          })
        );
        return;
      }
      setButtonLoader(true);
      let res = await PatchAPI(`admins/${adminId}`, changedFields)
      if (!res.error) {
        dispatch(
          showToast({
            type: "success",
            message: translate('Profile updated successfully')
          })
        );
        navigation("/view-profile");
      } else if (res.data.message === translate("Request failed with status code 409")) {
        dispatch(setProfileError({ [res.data.response.data.field]: translate(res.data.response.data.message) }));
      } else {
        dispatch(
          showToast({
            type: "error",
            message: res.data.message,
          })
        );
      }
    } catch (error) {
      console.error(translate("Error during admin creation:"), error);
    } finally {
      dispatch(setProfileLoader({ key: "phone_number", value: false }));
      setButtonLoader(false);
    }
  };

  const inputs = {
    "Personal Details": {
      "User Role": {
        required: false,
        disabled: true,
        id: "user_role",
        type: "input",
        tabIndex: 0,
      },
      "User Code": {
        required: false,
        disabled: true,
        id: "user_code",
        type: "input",
        tabIndex: 1,
      },
      "Official Email Address": {
        required: true,
        disabled: true,
        id: "email_address",
        maxLength: 90,
        type: "input",
        tabIndex: 4,
      },
      "First Name": {
        required: true,
        disabled: false,
        id: "first_name",
        maxLength: 60,
        type: "input",
        tabIndex: 2,
      },
      "Last Name": {
        required: false,
        disabled: false,
        maxLength: 60,
        id: "last_name",
        type: "input",
        tabIndex: 3,
      },
      "Phone Number": {
        required: true,
        disabled: false,
        id: "phone_number",
        type: "phone_number",
        maxLength: 15,
        tabIndex: 5,
      },
    },
    "Residential Details": {
      "House No.": {
        required: false,
        disabled: false,
        id: "house_no",
        type: "input",
        maxLength: 60,
        tabIndex: 6,
      },
      "Market/Block No.": {
        required: false,
        disabled: false,
        id: "market_block_no",
        type: "input",
        maxLength: 60,
        tabIndex: 7,
      },
      "Floor No.": {
        required: false,
        disabled: false,
        id: "floor_no",
        type: "input",
        tabIndex: 8,
        maxLength: 60,
      },
      "Street Name": {
        required: false,
        disabled: false,
        id: "street_name",
        type: "input",
        tabIndex: 9,
      },
      Country: {
        required: false,
        disabled: false,
        id: "country",
        type: "country_type",
        tabIndex: 10,
      },
    },
  };

  return (
    <div className="">
      <Breadcrumbs
        navPath={[
          "View Profile",
          "Edit Profile",
        ]}
        heading={`Edit Profile`}
        links={['/view-profile']}
      />
      <AddModel>
        <AddInputs
          editLoading={false}
          inputs={inputs}
          totalInputCount={9}
          verifyLoaders={loader}
          disableAll={disableAll}
          onInputChange={handleInputValues}
          inputValues={profileDetails}
          handleVerify={handleVerify}
          felidError={[]}
          errors={errors}
          handleSubmitOTP={handleSubmitOTP}
          isDisabledField={['user_role', 'user_code']}
          isOpenOtpModel={isOpenOtpModel}
          setIsOpenOtpModel={setIsOpenOtpModel}
          RequiredPostalCode={true}
          mapping={true}
        />
      </AddModel>
      <AddBottomButton
        buttonName={`Update`}
        buttonClass="w-[120px]"
        buttonWidth={`${!id ? 'w-[140px]' : 'w-[170px]'}`}
        onClick={handleSubmit}
        disabled={handleChanges(editData, profileDetails)}
        loading={buttonLoader}
        testID={"add_admin_page"}
      />
    </div>
  );
}

